/**
 * The fleet, the maintenance logs and the Invygo cars from the engine's PostgreSQL store (owner, 27 Sep 2026: "we
 * will not rely on Firebase anymore"; engine/api/fleet_routes.py), in the shapes the pages read from Firestore - so a
 * service changes its source, not its callers. Used only when REACT_APP_CONTRACTS_V2=true (USE_CONTRACTS_V2); the
 * live site keeps its Firestore reads and writes.
 *
 *   cars    GET /api/store/fleet         each car's fields + `vehicle_id` + `version` (never a renamed-plate pointer)
 *   logs    GET /api/store/maintenance   {id, ...log, version}
 *   Invygo  GET /api/store/invygo        {id, ...car, version}
 *
 * The saved copy (the browser's IndexedDB, jsonCacheService) keeps the engine's `asOf` of the read it came from: the
 * next read asks only what changed since (`?since=`), drops the `removed` keys from the copy FIRST and then lays the
 * changed items over it. A copy without `asOf` (saved from Firestore, or cleared) or not read whole for a day is read
 * whole again.
 *
 * A save names the `version` the record was opened with (the page's row; else the last read). One made on an older
 * version is refused by the engine (409, nothing written) and comes back as an Error with code 'record-changed' that
 * says who changed it and when (dd/mm/yyyy HH:MM, Dubai).
 */
import { finesService, officeError } from './finesService';
import { jsonCacheService } from './jsonCacheService';
import { cleanCarNames } from './fleetNames';
import { formatContractDateTime } from '../utils/contractDateUtils';

export const FLEET_FILE = 'fleet.json';
export const MAINTENANCE_FILE = 'maintenance.json';
export const INVYGO_FILE = 'invygo.json';

const WHOLE_READ_MIN = 24 * 60;        // a copy not read whole for a day is read whole again
const BATCH_SIZE = 500;                // writes per batch call (the engine takes up to 2000)
const IDS_PER_CALL = 1000;             // ids / plates per by-ids call (the engine's limit)
const META_KEYS = ['id', 'version', 'vehicle_id'];   // the store's own item keys: never written into a record

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());
/** Letters and digits, upper case ("CC 31047" -> "CC31047"): how the store compares plates. */
export const plateKey = (plate) => text(plate).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
/** The Invygo document id: the Booking ID without white space, upper case (as the engine keys it). */
export const invygoId = (v) => String(v ?? '').replace(/\s/g, '').toUpperCase();
const carKey = (car) => plateKey(car?.plate_number);
const idKey = (row) => String(row?.id ?? '');
const hasVersion = (v) => v !== undefined && v !== null;
const chunked = (list, size) => {
    const out = [];
    for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
    return out;
};
const minutesSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 60000 : Infinity);

const WHAT = {
    vehicles: 'car', maintenance_logs: 'maintenance log', service_inventory: 'inventory item',
    service_records: 'service record', invygo_cars: 'Invygo car',
};

/**
 * A failed store call as an Error the pages can show (finesService.officeError: `code`, `status`, the engine's own
 * words). A refused stale save (409 record-changed) says what was changed, by whom and when, and that nothing was saved.
 */
export const storeError = (e) => {
    if (e && e.storeError) return e;
    const err = typeof officeError === 'function' ? officeError(e) : Object.assign(new Error(e?.message || 'Request failed'), { cause: e });
    if (err.code === 'record-changed') {
        const data = e?.response?.data || {};
        const at = err.at ? formatContractDateTime(err.at) : '';
        const what = WHAT[data.table] || 'record';
        err.message = `This ${what} was changed by ${err.by || 'someone else'}${at ? ` on ${at}` : ''} after it was opened. `
            + 'Nothing was saved - the latest is loaded again; redo the change.';
    }
    err.storeError = true;
    return err;
};

/** A page's fields as a write: without the store's own keys, the write stamp and `skip` keys (the page's own flags). */
export const writableFields = (fields, skip = []) => {
    const out = {};
    Object.entries(fields || {}).forEach(([k, v]) => {
        if (META_KEYS.includes(k) || k === 'last_updated' || skip.includes(k) || v === undefined) return;
        out[k] = v;
    });
    return out;
};

// ---------------------------------------------------------------------------------------- the saved copy + since
/**
 * Every row of one list, through its saved copy: a copy with the engine's `asOf` (read whole within a day) asks only
 * what changed since - `removed` dropped first, then the changed items laid over (an item that no longer passes
 * `keep` leaves the copy); otherwise one whole read. `read(since)` answers {items, removed, asOf}. A failed read
 * throws (the caller decides on its fallback).
 */
export async function syncedRead({ file, read, keyOf, removedKeyOf = (k) => String(k), keep = () => true, force = false }) {
    const saved = force ? null : await jsonCacheService.loadFromFile(file);
    const rows = saved && Array.isArray(saved.data) ? saved.data : null;
    if (rows && saved.asOf && minutesSince(saved.fullReadAt) < WHOLE_READ_MIN) {
        const answer = await read(saved.asOf);
        const byKey = new Map(rows.map((r) => [keyOf(r), r]));
        (answer?.removed || []).forEach((k) => byKey.delete(removedKeyOf(k)));
        (answer?.items || []).forEach((item) => {
            if (keep(item)) byKey.set(keyOf(item), item);
            else byKey.delete(keyOf(item));
        });
        const merged = [...byKey.values()];
        await jsonCacheService.saveToFile(file, merged, { asOf: answer?.asOf || saved.asOf });
        return merged;
    }
    const answer = await read(undefined);
    const items = (answer?.items || []).filter(keep);
    await jsonCacheService.saveToFile(file, items, { full: true, asOf: answer?.asOf || null });
    return items;
}

// the versions of the last read: a save that is not told the version it was opened with uses these
const carVersions = new Map();         // plate key -> version
const logVersions = new Map();         // log id -> version
const remember = (map, rows, keyOf) => {
    map.clear();
    (rows || []).forEach((r) => { if (r && hasVersion(r.version)) map.set(keyOf(r), r.version); });
};

// ------------------------------------------------------------------------------------------------------ cars
const isCar = (car) => !!car && !car.moved_to;

/** Every car (the saved copy + what changed since its read). */
export async function storeCars({ force = false } = {}) {
    const cars = await syncedRead({
        file: FLEET_FILE,
        force,
        read: (since) => finesService.storeFleet({ since }),
        keyOf: carKey,
        removedKeyOf: plateKey,
        keep: isCar,
    });
    remember(carVersions, cars, carKey);
    return cars;
}

/** The version of a car in the last read (undefined: not read). */
export const carVersionOf = (plate) => carVersions.get(plateKey(plate));

/** The car behind a plate - also an OLD plate (the engine follows the plate history); null when none. */
export async function storeCarByPlate(plate) {
    if (!text(plate)) return null;
    try {
        const car = await finesService.storeFleetByPlate(text(plate));
        return isCar(car) ? car : null;
    } catch (e) {
        if (e?.response?.status === 404) return null;
        throw storeError(e);
    }
}

/** Map(plate as asked -> the car carrying it now); a plate no car carries now is left out. */
export async function storeCarsByPlates(plates) {
    const wanted = [...new Set((plates || []).map(text).filter(Boolean))];
    const found = new Map();
    for (const chunk of chunked(wanted, IDS_PER_CALL)) {
        let items;
        try {
            items = await finesService.storeFleetByPlates(chunk);
        } catch (e) {
            throw storeError(e);
        }
        Object.entries(items || {}).forEach(([plate, car]) => { if (isCar(car)) found.set(text(plate), car); });
    }
    return found;
}

/** One car's fields merged, if it is still at `version` (default: the last read) -> the car as saved. */
export async function storeSaveCar(plate, changes, version) {
    let v = hasVersion(version) ? version : carVersionOf(plate);
    if (!hasVersion(v)) {
        const car = await storeCarByPlate(plate);
        if (!car) throw Object.assign(new Error(`No car with the plate ${text(plate)}.`), { code: 'not-found', storeError: true });
        v = car.version;
    }
    try {
        const car = await finesService.storeFleetSave(text(plate), writableFields(cleanCarNames(changes)), v);
        if (car && hasVersion(car.version)) carVersions.set(plateKey(plate), car.version);
        return car;
    } catch (e) {
        throw storeError(e);
    }
}

const COUNT_KEYS = ['updated', 'unchanged'];   // the engine's batch is updates only (28 Sep 2026)

/**
 * Car updates through POST /fleet/batch, BATCH_SIZE a call (one transaction each): `{op: 'update', plate, changes,
 * version}`. An update without a version takes the car's version of the last read, else the car's version now; a
 * car that cannot be found is listed in `notFound` without being sent. Returns the engine's counts summed,
 * `conflicts` / `notFound` with `index` = the position in `writes`. A refused call throws (`partial`: what the calls
 * before it wrote).
 */
export async function storeCarBatch(writes, { onChunk } = {}) {
    const list = (writes || []).map((w, index) => ({ w: { ...w, op: w.op || 'update' }, index }));
    const out = { updated: 0, unchanged: 0, conflicts: [], notFound: [] };
    const needsVersion = (w) => w.op === 'update' && !hasVersion(w.version);
    const unknown = list.filter(({ w }) => needsVersion(w) && !hasVersion(carVersionOf(w.plate))).map(({ w }) => w.plate);
    const now = unknown.length ? await storeCarsByPlates(unknown) : new Map();
    const sendable = [];
    list.forEach((p) => {
        if (!needsVersion(p.w)) { sendable.push(p); return; }
        const v = hasVersion(carVersionOf(p.w.plate)) ? carVersionOf(p.w.plate) : now.get(text(p.w.plate))?.version;
        if (hasVersion(v)) sendable.push({ ...p, w: { ...p.w, version: v } });
        else out.notFound.push({ index: p.index, plate: p.w.plate });
    });
    let done = 0;
    for (const chunk of chunked(sendable, BATCH_SIZE)) {
        let answer;
        try {
            answer = await finesService.storeFleetBatch(chunk.map((p) => p.w));
        } catch (e) {
            throw Object.assign(storeError(e), { partial: out });
        }
        COUNT_KEYS.forEach((k) => { out[k] += Number(answer?.[k]) || 0; });
        (answer?.conflicts || []).forEach((c) => {
            const p = chunk[c.index];
            out.conflicts.push({ ...c, index: p ? p.index : c.index, plate: p ? p.w.plate : c.plate });
        });
        (answer?.notFound || []).forEach((c) => {
            const p = chunk[c.index];
            out.notFound.push({ index: p ? p.index : c.index, plate: p ? p.w.plate : c.plate });
        });
        done += chunk.length;
        if (onChunk) onChunk(done, sendable.length);
    }
    return out;
}

/** "N car(s) were not saved ..." for the conflicts of a batch ('' when none). */
export const conflictText = (conflicts) => {
    const list = conflicts || [];
    if (!list.length) return '';
    const plates = list.slice(0, 10).map((c) => c.plate).join(', ');
    const more = list.length > 10 ? ` and ${list.length - 10} more` : '';
    const taken = list.filter((c) => c.why === 'a car carries this plate').length;
    return `${list.length} car(s) were not saved (${plates}${more}): ${taken === list.length
        ? 'a car already carries that plate'
        : 'changed by someone else since the list was read'}. The latest is loaded again - redo the change for them.`;
};

/** A car's fields for an `update`: names cleaned, without the store's keys, the write stamp and the plate itself. */
const updateFields = (car) => writableFields(cleanCarNames(car), ['plate_number', 'plate']);
const plateOf = (car) => text(car?.plate_number ?? car?.plate);

/**
 * Fleet Manager's bulk edits (fleetService.executeMasterSync) as batch writes: each update merges its fields (a write
 * whose only field is the plate is left out); `version` rides along when the write carries it. Updates only - the
 * engine takes nothing else (the archive / add writes of the removed Fleet file and RTA master list imports went with
 * them: owner, 28 Sep 2026).
 */
export const masterSyncWrites = (updates = []) => (updates || [])
    .map((car) => ({ op: 'update', plate: plateOf(car), changes: updateFields(car), ...(hasVersion(car?.version) ? { version: car.version } : {}) }))
    .filter((w) => w.plate && Object.keys(w.changes).length);

// ------------------------------------------------------------------------------------------------------ maintenance
/** Every maintenance log (the saved copy + what changed since its read: closed by the engine, edited, deleted). */
export async function storeLogs({ force = false } = {}) {
    const logs = await syncedRead({
        file: MAINTENANCE_FILE,
        force,
        read: (since) => finesService.storeMaintenance({ since }),
        keyOf: idKey,
    });
    remember(logVersions, logs, idKey);
    return logs;
}

export const logVersionOf = (id) => logVersions.get(String(id ?? ''));

/** The page's own keys on a log row (never written). */
const LOG_PAGE_KEYS = ['isDb', 'data_hash', 'originalIndex', 'Index', 'Days Delayed'];

/** A new log - or the same repair (plate | damage | Date OUT) already logged, updated: {id, ...log, version, wasCreated | wasUpdated}. */
export async function storeAddLog(log) {
    try {
        const saved = await finesService.storeMaintenanceAdd(writableFields(log, LOG_PAGE_KEYS));
        if (saved && hasVersion(saved.version)) logVersions.set(String(saved.id), saved.version);
        return saved;
    } catch (e) {
        throw storeError(e);
    }
}

/** A log's fields merged, if it is still at `version` (default: the last read) -> the log as saved. */
export async function storeSaveLog(id, changes, version) {
    let v = hasVersion(version) ? version : logVersionOf(id);
    if (!hasVersion(v)) {
        await storeLogs();
        v = logVersionOf(id);
    }
    if (!hasVersion(v)) throw Object.assign(new Error('This maintenance log no longer exists.'), { code: 'not-found', storeError: true });
    try {
        const saved = await finesService.storeMaintenanceSave(String(id), writableFields(changes, LOG_PAGE_KEYS), v);
        if (saved && hasVersion(saved.version)) logVersions.set(String(id), saved.version);
        return saved;
    } catch (e) {
        throw storeError(e);
    }
}

/** Delete a log (already gone: nothing to do); `version` checked when given. */
export async function storeDeleteLog(id, version) {
    try {
        const answer = await finesService.storeMaintenanceDelete(String(id), hasVersion(version) ? version : undefined);
        logVersions.delete(String(id));
        return answer;
    } catch (e) {
        throw storeError(e);
    }
}

// ------------------------------------------------------------------------------------------------------ Invygo
// The old Invygo sheet's cars: history, read only - the sheet is no longer uploaded (owner, 28 Sep 2026); a new
// Invygo booking's id comes from Ejar's bookings (src/services/invygoIds.js).
/** Every Invygo car (the saved copy + what changed since its read). */
export const storeInvygoCars = ({ force = false } = {}) => syncedRead({
    file: INVYGO_FILE,
    force,
    read: (since) => finesService.storeInvygo({ since }),
    keyOf: idKey,
});

/** Map(id -> car) of these Booking IDs (keyed by invygoId). */
export async function storeInvygoByIds(ids) {
    const wanted = [...new Set((ids || []).map(invygoId).filter(Boolean))];
    const found = new Map();
    for (const chunk of chunked(wanted, IDS_PER_CALL)) {
        let items;
        try {
            items = await finesService.storeInvygoByIds(chunk);
        } catch (e) {
            throw storeError(e);
        }
        Object.entries(items || {}).forEach(([id, car]) => found.set(invygoId(id), car));
    }
    return found;
}
